import { useState } from 'react';
import { api } from '@/api/client';
import { X, Loader2, Image } from 'lucide-react';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import StarRating from './StarRating';

export default function EditReviewModal({ review, onClose, onSubmitted }) {
  useBodyScrollLock();
  const [rating, setRating] = useState(review.rating);
  const [comment, setComment] = useState(review.comment || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(review.photo_url || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (rating === 0) { setError('Please select a rating.'); return; }
    setSubmitting(true);
    setError('');
    try {
      let photo_url = photoPreview;
      if (photoFile) {
        const { file_url } = await api.integrations.Core.UploadFile({ file: photoFile });
        photo_url = file_url;
      }
      await api.entities.Review.update(review.id, {
        rating,
        comment,
        photo_url,
      });
      onSubmitted();
    } catch (err) {
      console.error('Failed to save the review', err);
      setError(err?.message || 'Could not save your review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-foreground/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full bg-card rounded-t-3xl border-t border-border p-5 pb-10 space-y-5 max-h-[90vh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="w-10 h-1 bg-border rounded-full mx-auto" />

        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">Edit Review</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X size={20} />
          </button>
        </div>

        {/* Rating */}
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rating *</label>
          <StarRating rating={rating} size={32} interactive onChange={setRating} showLabel />
        </div>

        {/* Comment */}
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Comment</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your experience..."
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-input bg-background text-base md:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
        </div>

        {/* Photo */}
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Photo (optional)</label>
          {photoPreview ? (
            <div className="relative">
              <img src={photoPreview} alt="Preview" className="max-h-56 max-w-[280px] object-contain rounded-xl" />
              <button
                onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                className="absolute top-2 right-2 w-7 h-7 bg-card/80 backdrop-blur-sm rounded-full flex items-center justify-center border border-border"
              >
                <X size={13} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center gap-2 h-28 border-2 border-dashed border-border rounded-xl cursor-pointer hover:bg-secondary/50 transition-colors">
              <Image size={20} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Tap to add a photo</span>
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            </label>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-3.5 bg-primary text-primary-foreground rounded-2xl font-semibold text-sm shadow-sm hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {submitting ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}